import { Alert, Button, Form, Input, InputNumber, Modal, Switch } from '@arco-design/web-react'

import SettingSectionCard from '../SettingSectionCard'

import SensitiveInput from '../SensitiveInput'

import DingTalkWebhookTestButton from './DingTalkWebhookTestButton'

import type { OptionsAPI } from '../useOptions'



const ALERT_KEYS = [

  'alert_setting.alert_enabled',

  'alert_setting.environment',

  'alert_setting.dingtalk_webhook_url',

  'alert_setting.dingtalk_secret',

  'alert_setting.mention_mobiles',

  'alert_setting.cooldown_seconds',

  'alert_setting.dedup_window_seconds',

  'alert_setting.silence_enabled',

  'alert_setting.silence_start',

  'alert_setting.silence_end',

  'alert_setting.cert_monitor_enabled',

  'alert_setting.cert_monitor_domains',

  'alert_setting.error_log_enabled',

  'alert_setting.structured_log_enabled',

] as const



export default function AlertSettingCard({ options }: { options: OptionsAPI }) {

  const { get, getBool, getNumber, patch, clearSensitive, saving } = options

  const alertEnabled = getBool('alert_setting.alert_enabled')

  const silenceEnabled = getBool('alert_setting.silence_enabled')



  const confirmClear = (key: string, label: string) => {

    Modal.confirm({

      title: `清空${label}？`,

      content: '清空后将删除后端已保存的配置，需重新填写才能恢复告警。',

      okButtonProps: { status: 'danger' },

      onOk: () => clearSensitive(key),

    })

  }



  return (

    <SettingSectionCard title="告警通知" subtitle="钉钉 Webhook、去重冷却与运行日志开关" keys={[...ALERT_KEYS]} options={options}>

      <Alert type="info" className="mb-4" content="Webhook 与 Secret 留空保存时不覆盖已有配置；需点击「清空」才会删除后端值。错误日志开关不影响告警与 metrics。证书巡检每 6 小时探测一次 443，剩余 30 天 / 7 天各提醒一次。" />

      <Form layout="vertical">

        <div className="grid md:grid-cols-2 gap-3">

          <Form.Item label="启用告警">

            <Switch checked={alertEnabled} onChange={(v) => patch({ 'alert_setting.alert_enabled': v })} />

          </Form.Item>

          <Form.Item
            label="余额告警全局 Webhook"
            extra="渠道未单独填 Webhook 时使用；支持钉钉/自定义机器人"
          >
            <Input
              value={get('channel_balance_setting.ChannelBalanceWebhookURL')}
              onChange={(v) => patch({ 'channel_balance_setting.ChannelBalanceWebhookURL': v })}
              placeholder="https://oapi.dingtalk.com/robot/send?access_token=..."
            />
          </Form.Item>
          <Form.Item label="余额告警冷却（分钟）" extra="同一渠道两次提醒的最小间隔">
            <InputNumber
              style={{ width: '100%' }}
              min={1}
              value={getNumber('channel_balance_setting.ChannelBalanceAlertCooldownMinutes', 60)}
              onChange={(v) =>
                patch({ 'channel_balance_setting.ChannelBalanceAlertCooldownMinutes': v ?? 60 })
              }
            />
          </Form.Item>
          <Form.Item label="余额告警阈值（USD）" extra="全局默认阈值；单渠道可覆盖">
            <InputNumber
              style={{ width: '100%' }}
              min={0}
              value={getNumber('alert_setting.channel_balance_low_threshold', 0)}
              onChange={(v) => patch({ 'alert_setting.channel_balance_low_threshold': v ?? 0 })}
            />
          </Form.Item>
          <Form.Item label="环境标识">

            <Input value={get('alert_setting.environment')} onChange={(v) => patch({ 'alert_setting.environment': v })} placeholder="production" />

          </Form.Item>

          <Form.Item label="钉钉 Webhook URL">

            <div className="flex gap-2">

              <div className="flex-1 min-w-0">

                <SensitiveInput value={get('alert_setting.dingtalk_webhook_url')} onChange={(v) => patch({ 'alert_setting.dingtalk_webhook_url': v })} />

              </div>

              <Button size="small" status="danger" loading={saving} onClick={() => confirmClear('alert_setting.dingtalk_webhook_url', 'Webhook URL')}>

                清空

              </Button>

            </div>

          </Form.Item>

          <Form.Item label="钉钉加签 Secret">

            <div className="flex gap-2">

              <div className="flex-1 min-w-0">

                <SensitiveInput value={get('alert_setting.dingtalk_secret')} onChange={(v) => patch({ 'alert_setting.dingtalk_secret': v })} />

              </div>

              <Button size="small" status="danger" loading={saving} onClick={() => confirmClear('alert_setting.dingtalk_secret', 'Secret')}>

                清空

              </Button>

            </div>

          </Form.Item>

          <Form.Item label="@ 手机号（逗号分隔）">

            <Input value={get('alert_setting.mention_mobiles')} onChange={(v) => patch({ 'alert_setting.mention_mobiles': v })} allowClear />

          </Form.Item>

          <Form.Item label="冷却时间 (秒)">

            <InputNumber style={{ width: '100%' }} min={60} max={86400} value={getNumber('alert_setting.cooldown_seconds', 300)} onChange={(v) => patch({ 'alert_setting.cooldown_seconds': v ?? 300 })} />

          </Form.Item>

          <Form.Item label="去重窗口 (秒)">

            <InputNumber style={{ width: '100%' }} min={60} max={86400} value={getNumber('alert_setting.dedup_window_seconds', 300)} onChange={(v) => patch({ 'alert_setting.dedup_window_seconds': v ?? 300 })} />

          </Form.Item>

          <Form.Item label="静默时段">

            <Switch checked={silenceEnabled} onChange={(v) => patch({ 'alert_setting.silence_enabled': v })} />

          </Form.Item>

          <Form.Item label="静默开始 (HH:mm)">

            <Input disabled={!silenceEnabled} value={get('alert_setting.silence_start')} onChange={(v) => patch({ 'alert_setting.silence_start': v })} placeholder="23:00" />

          </Form.Item>

          <Form.Item label="静默结束 (HH:mm)">

            <Input disabled={!silenceEnabled} value={get('alert_setting.silence_end')} onChange={(v) => patch({ 'alert_setting.silence_end': v })} placeholder="08:00" />

          </Form.Item>

          <Form.Item label="TLS 证书到期巡检">

            <Switch

              checked={getBool('alert_setting.cert_monitor_enabled', true)}

              onChange={(v) => patch({ 'alert_setting.cert_monitor_enabled': v })}

            />

          </Form.Item>

          <Form.Item label="巡检域名（逗号分隔）">

            <Input

              value={get('alert_setting.cert_monitor_domains')}

              onChange={(v) => patch({ 'alert_setting.cert_monitor_domains': v })}

              placeholder="tokentcp.com,api.tokentcp.com（留空取 ServerAddress 域名）"

            />

          </Form.Item>

          <Form.Item label="写入错误消费日志">

            <Switch checked={getBool('alert_setting.error_log_enabled')} onChange={(v) => patch({ 'alert_setting.error_log_enabled': v })} />

          </Form.Item>

          <Form.Item label="JSON 结构化运行日志">

            <Switch checked={getBool('alert_setting.structured_log_enabled')} onChange={(v) => patch({ 'alert_setting.structured_log_enabled': v })} />

          </Form.Item>

        </div>

        <DingTalkWebhookTestButton options={options} />

      </Form>

    </SettingSectionCard>

  )

}

